import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogIn, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TwoFactorModal from '../components/TwoFactorModal';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [verifying, setVerifying] = useState(false);
  const { login, verify2FA, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      if (result.requires2FA) {
        // Show 2FA modal
        setTwoFAEmail(result.email);
        setShow2FAModal(true);
        toast.success(result.message || 'Verifiseringskode sendt til din e-post');
      } else {
        // Direct login (fallback for non-2FA)
        toast.success('Innlogget! ✅');
        navigate('/admin/dashboard');
      }
    } else {
      toast.error(result.error || 'Innlogging feilet');
    }

    setLoading(false);
  };

  const handle2FAVerify = async (code) => {
    setVerifying(true);
    const result = await verify2FA(twoFAEmail, code);

    if (result.success) {
      toast.success('2FA verifisert! Logger inn...');
      setShow2FAModal(false);
      navigate('/admin/dashboard');
    } else {
      toast.error(result.error || 'Ugyldig kode');
    }

    setVerifying(false);
  };

  const handle2FAClose = () => {
    setShow2FAModal(false);
    setTwoFAEmail('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-600/10 rounded-full blur-3xl"></div>
      </div>

      <Card className="w-full max-w-md bg-gray-900/80 border-gray-700 backdrop-blur-xl relative z-10">
        <CardHeader className="space-y-4 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img 
              src="/images/sand-tec-logo-light.png"
              alt="Sand-tec AS Logo"
              className="h-20 w-auto"
              style={{ 
                filter: 'invert(1) hue-rotate(180deg) brightness(0.95) saturate(1.2)',
                mixBlendMode: 'screen'
              }}
            />
          </div>
          
          <CardTitle className="text-3xl font-black">
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
              Admin Innlogging
            </span>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Logg inn for å administrere Sand-tec AS
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">E-post</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@sand-tec.no"
                required
                className="bg-gray-800 border-gray-700 text-white focus:border-red-600"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Passord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-gray-800 border-gray-700 text-white focus:border-red-600"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg py-6 rounded-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Logger inn...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Logg Inn
                </>
              )}
            </Button>

            <div className="text-center">
              <a
                href="/forgot-password"
                className="text-sm text-gray-400 hover:text-red-400 transition-colors"
              >
                Glemt passord?
              </a>
            </div>
          </form>

          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-sm text-gray-400 hover:text-red-400 transition-colors duration-300"
            >
              ← Tilbake til hjemmesiden
            </a>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Modal */}
      <TwoFactorModal
        isOpen={show2FAModal}
        onClose={handle2FAClose}
        email={twoFAEmail}
        onVerify={handle2FAVerify}
        loading={verifying}
      />
    </div>
  );
};

export default Login;